import { bindable, BindingMode, customElement } from 'aurelia';
import template from './user-input.html';
import { searchUsers } from '../services/api.service';
import type { SharedUser } from '../services/api.types';

@customElement({ name: 'user-input', template })
export class UserInput {
  @bindable({ mode: BindingMode.twoWay }) users: SharedUser[] = [];
  @bindable readonly: boolean = false;
  @bindable currentUsername: string = '';

  inputValue = '';
  suggestions: SharedUser[] = [];
  showDropdown = false;
  highlightedIndex = -1;

  private _debounce: ReturnType<typeof setTimeout> | null = null;
  private _currentQuery = '';
  private _portal: HTMLUListElement | null = null;
  chipsRef!: HTMLElement;
  private _positionHandler = () => this._positionPortal();
  private _touchMoveHandler = (e: TouchEvent) => {
    if (this._portal && !this._portal.contains(e.target as Node)) {
      this._clearDropdown();
    }
  };

  attached() {
    this._portal = document.createElement('ul');
    this._portal.className = 'user-suggestions';
    this._portal.style.display = 'none';
    this._portal.style.position = 'absolute';
    this._portal.style.zIndex = '9999';
    this._portal.addEventListener('mousedown', (e) => this._onPortalMouseDown(e));
    this._portal.addEventListener('touchstart', (e) => this._onPortalTouchStart(e), { passive: false });
    document.body.appendChild(this._portal);
    window.addEventListener('scroll', this._positionHandler, true);
    window.addEventListener('resize', this._positionHandler);
    window.addEventListener('touchmove', this._touchMoveHandler, { capture: true, passive: true });
  }

  detaching() {
    this._portal?.remove();
    this._portal = null;
    window.removeEventListener('scroll', this._positionHandler, true);
    window.removeEventListener('resize', this._positionHandler);
    window.removeEventListener('touchmove', this._touchMoveHandler, true);
  }

  focusInput() {
    this.chipsRef?.querySelector<HTMLInputElement>('.user-input-field')?.focus();
  }

  onInput(event: Event) {
    const val = (event.target as HTMLInputElement).value;
    this.inputValue = val;
    this.highlightedIndex = -1;
    if (this._debounce) clearTimeout(this._debounce);
    if (!val.trim()) {
      this._clearDropdown();
      return;
    }
    this._debounce = setTimeout(() => { void this._doSearch(val); }, 200);
  }

  onKeyDown(event: KeyboardEvent): boolean {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.showDropdown && this.suggestions.length > 0) {
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.suggestions.length - 1);
        this._renderPortal();
      }
      return false;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.showDropdown && this.suggestions.length > 0) {
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        this._renderPortal();
      }
      return false;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.showDropdown && this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
        this.addUser(this.suggestions[this.highlightedIndex]);
      }
      return false;
    }
    if (event.key === 'Tab') {
      if (this.showDropdown && this.suggestions.length > 0) {
        const idx = this.highlightedIndex >= 0 ? this.highlightedIndex : (this.suggestions.length === 1 ? 0 : -1);
        if (idx >= 0) {
          event.preventDefault();
          this.addUser(this.suggestions[idx]);
          return false;
        }
      }
      return true;
    }
    if (event.key === 'Escape') {
      this._clearDropdown();
      return false;
    }
    if (event.key === 'Backspace' && this.inputValue === '' && this.users.length > 0) {
      this.users = this.users.slice(0, -1);
      return false;
    }
    return true;
  }

  addUser(user: SharedUser) {
    if (!this.users.some(u => u.username.toLowerCase() === user.username.toLowerCase())) {
      this.users = [...this.users, user];
    }
    this.inputValue = '';
    this._clearDropdown();
    this.focusInput();
  }

  removeUser(user: SharedUser) {
    this.users = this.users.filter(u => u.username !== user.username);
  }

  hideDropdown() {
    setTimeout(() => this._clearDropdown(), 150);
  }

  private async _doSearch(query: string) {
    this._currentQuery = query;
    const matches = await searchUsers(query);
    if (query !== this._currentQuery) return;

    const filtered = matches.filter(user =>
      !this.users.some(u => u.username.toLowerCase() === user.username.toLowerCase()) &&
      user.username.toLowerCase() !== this.currentUsername.toLowerCase());

    this.suggestions = filtered;
    this.showDropdown = filtered.length > 0;
    this.highlightedIndex = -1;
    this._renderPortal();
  }

  private _clearDropdown() {
    this.showDropdown = false;
    this.highlightedIndex = -1;
    this.suggestions = [];
    this._currentQuery = '';
    this._renderPortal();
  }

  private _positionPortal() {
    if (!this._portal || !this.chipsRef || !this.showDropdown) return;
    const rect = this.chipsRef.getBoundingClientRect();
    const scrollTop = window.scrollY ?? document.documentElement.scrollTop;
    const scrollLeft = window.scrollX ?? document.documentElement.scrollLeft;
    this._portal.style.top = `${rect.bottom + scrollTop + 4}px`;
    this._portal.style.left = `${rect.left + scrollLeft}px`;
    this._portal.style.width = `${rect.width}px`;
  }

  private _renderPortal() {
    if (!this._portal) return;
    if (!this.showDropdown || this.suggestions.length === 0) {
      this._portal.style.display = 'none';
      return;
    }

    this._positionPortal();
    this._portal.style.display = '';
    this._portal.innerHTML = this.suggestions.map((user, i) => {
      const active = i === this.highlightedIndex ? ' user-suggestion-item--active' : '';
      const displayName = user.displayName && user.displayName !== user.username
        ? `<span class="user-suggestion-display-name">${this._esc(user.displayName)}</span>`
        : '';
      return `<li class="user-suggestion-item${active}" data-idx="${i}">` +
        `<span class="user-suggestion-name">${this._esc(user.username)}</span>${displayName}</li>`;
    }).join('');

    const activeEl = this._portal.querySelector<HTMLElement>('.user-suggestion-item--active');
    activeEl?.scrollIntoView({ block: 'nearest' });
  }

  private _onPortalMouseDown(e: MouseEvent) {
    const li = (e.target as Element).closest('[data-idx]') as HTMLElement | null;
    if (!li) return;
    e.preventDefault();
    const idx = parseInt(li.dataset['idx'] ?? '-1', 10);
    if (idx >= 0 && idx < this.suggestions.length) this.addUser(this.suggestions[idx]);
  }

  private _onPortalTouchStart(e: TouchEvent) {
    const li = (e.target as Element).closest('[data-idx]') as HTMLElement | null;
    if (!li) return;
    // Prevent the touch from becoming a mousedown that triggers outside-click handlers
    e.preventDefault();
    const idx = parseInt(li.dataset['idx'] ?? '-1', 10);
    if (idx >= 0 && idx < this.suggestions.length) this.addUser(this.suggestions[idx]);
  }

  private _esc(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
